import { CommonStorageFacilitySerializer } from '@bcba/serializers';
import { FormSection } from '../../DigitalDossier/DigitalDossierSection/components/FormSection';
import { useTranslation } from '@bcba/react-hooks';
import {
  Accordion,
  AccordionItem,
  EmptyState,
  FiSearch,
  Grid,
  GridItem,
  ReadOnlyField,
  ReadOnlyTextarea,
  Stack,
} from '@agrotoken-tech/ui';
import { formatDate } from '@bcba/common-utils';

interface Props {
  data: CommonStorageFacilitySerializer[];
}

export const ReadOnlyStorageAffidavitsSection = ({ data }: Props) => {
  const { t } = useTranslation();

  return (
    <FormSection title={t('explorer.storageFacilities.title')}>
      {data.length ? (
        <Accordion allowToggle>
          {data.map((facility, facilityIndex) => {
            const affidavit = facility.storageAffidavit;
            return (
              <AccordionItem
                key={facility.uuid}
                title={t('storageFacilities.accordion.title').replace(
                  '{{n}}',
                  String(facilityIndex + 1),
                )}
              >
                <Stack gap={4}>
                  <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
                    <ReadOnlyField
                      label={t('storageFacilities.upsert.identifier.label')}
                      value={facility.identifier}
                    />
                    <ReadOnlyField
                      label={t('storageFacilities.upsert.ownership.label')}
                      value={t(`ownershipType.${facility.ownershipType}`)}
                    />
                    <ReadOnlyField
                      label={t('storageFacilities.upsert.province.label')}
                      value={facility.province.name}
                    />
                    <ReadOnlyField
                      label={t('storageFacilities.upsert.locality.label')}
                      value={facility.locality.name}
                    />

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormSection title={t('common.affidavits')}>
                        {affidavit && (
                          <Accordion defaultIndex={0}>
                            <AccordionItem
                              title={t(
                                'storageFacilities.upsert.newAffidavit.title',
                              )}
                            >
                              <Grid
                                templateColumns={{ md: 'repeat(2, 1fr)' }}
                                gap={6}
                              >
                                <ReadOnlyField
                                  label={t(
                                    'storageFacilities.upsert.storageCapacity.label',
                                  )}
                                  value={affidavit.capacity}
                                />
                                <ReadOnlyField
                                  value={
                                    affidavit.rucaStatus === null
                                      ? '-'
                                      : t(`rucaStatus.${affidavit.rucaStatus}`)
                                  }
                                  label={t('common.RUCA')}
                                />
                                <ReadOnlyField
                                  label={t(
                                    'storageFacilities.upsert.startDate.label',
                                  )}
                                  value={formatDate(affidavit.startDate)}
                                />
                                <ReadOnlyField
                                  label={t(
                                    'storageFacilities.upsert.endDate.label',
                                  )}
                                  value={formatDate(affidavit.endDate)}
                                />
                                <GridItem colSpan={{ base: 1, md: 2 }}>
                                  <ReadOnlyTextarea
                                    label={t(
                                      'storageFacilities.upsert.observations.label',
                                    )}
                                    value={affidavit.observations ?? '-'}
                                  />
                                </GridItem>
                              </Grid>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </FormSection>
                    </GridItem>
                  </Grid>
                </Stack>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <EmptyState
          title={t(
            'sharedDigitalDossier.affidavits.storageFacilities.emptyState.title',
          )}
          description={t(
            'sharedDigitalDossier.affidavits.storageFacilities.emptyState.description',
          )}
          size="md"
          icon={FiSearch}
        />
      )}
    </FormSection>
  );
};
